import { Component, Input, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { ModalSize } from "./modal-size.enum";

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss']
})

export class ModalComponent implements OnDestroy {
  visible = false;
  @Input() customClassName?= '';
  @Input() scrollable = false;
  @Input() size: ModalSize = ModalSize.Sm;

  @Input() deleteIndex = -1;
  @Input() deleteId: string;

  id = this.getRandomInt();
  private keydownListener: (event: KeyboardEvent) => void;

  constructor(private renderer: Renderer2, private zone: NgZone) {
    // Run event listener outside Angular zone to prevent triggering change detection
    this.zone.runOutsideAngular(() => {
      this.keydownListener = (event: KeyboardEvent) => {
        // Only process if this modal is visible
        if (!this.visible) {
          return;
        }

        if (event.key.toUpperCase() === 'ESCAPE') {
          event.preventDefault();
          const modalStack = this.getModalStack();
          if (modalStack.length && modalStack[0] == this.id) {
            // Run hide() inside Angular zone since it updates component state
            this.zone.run(() => {
              this.hide();
            });
          }
        }
      };
      document.addEventListener('keydown', this.keydownListener);
    });
  }

  ngOnDestroy() {
    // Clean up event listener
    document.removeEventListener('keydown', this.keydownListener);
  }

  hide() {
    this.visible = false;
    this.removeFromModalStack();

    this.setOpenModalCount(-1);
    if (this.getOpenModalCount() <= 0) {
      // only remove the modal scroll prevention styling when the LAST modal is closed
      this.renderer.removeClass(document.body, 'modal-open');
    }
  }

  show() {
    this.visible = true;
    this.setOpenModalCount(+1)
    this.addToModalStack();
    this.renderer.addClass(document.body, 'modal-open');
  }

  showUsingDeleteIndex(index: number) {
    this.deleteIndex = index;
    this.show();
  }

  showUsingDeleteId(id: string) {
    this.deleteId = id;
    this.show();
  }

  resetDeleteIndexAndId() {
    this.deleteIndex = -1;
    this.deleteId = null;
  }

  private setOpenModalCount = (change) => {
    document.body.dataset.openmodals = (+(document.body.dataset.openmodals || '0') + change).toString();
  }

  private getOpenModalCount = () => {
    return +document.body.dataset.openmodals;
  }

  private addToModalStack = () => {
    const modalStack = this.getModalStack();
    modalStack.unshift(this.id)
    document.body.dataset.modalstack = JSON.stringify(modalStack);
  }

  private removeFromModalStack = () => {
    const modalStack = this.getModalStack();
    modalStack.shift();
    document.body.dataset.modalstack = JSON.stringify(modalStack);
  }

  private getModalStack = () => {
    return JSON.parse(document.body.dataset.modalstack || '[]') || [];
  }

  private getRandomInt() {
    return Math.floor(Math.random() * 999999);
  }

  protected readonly ModalSize = ModalSize;
}
